import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ShieldAlert, Plus, X, RefreshCw, Loader, Trash2, ExternalLink, Save, Search,
  FileText, MapPin, Calendar, AlertTriangle, Ban, User, Users, Building, UploadCloud, Check, ScanLine, Camera, Send,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import MailMonitorReturn from './MailMonitorReturn';

const MAX_MB = 4; // base64 inflates ~33%; Netlify function body limit is 6 MB

function arrayBufferToBase64(ab) {
  const bytes = new Uint8Array(ab);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
}
async function fileToBase64(file) {
  return { base64: arrayBufferToBase64(await file.arrayBuffer()), contentType: file.type || 'application/octet-stream', name: file.name };
}
const isAllowedFile = (f) => /pdf$/i.test(f.type) || /^image\//i.test(f.type);

// ── Mail Stoppers ─────────────────────────────────────────────────────────────
// Admin register of AP Mail Stopper / Monitor requests (Customs / ABF / NSW Police).
// Emails are read into mail_stoppers by the inbound webhook; this page lists them,
// shows the original PDF, lets an admin add one manually, correct a mis-parsed row,
// and mark it cancelled/expired. Driver match + portal alert land in Phase 2.

const URL = '/.netlify/functions/mail-stopper-admin-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const suggestion = (r) => (r && r.extracted && r.extracted.matchSuggestion) || {};
const oneLineAddr = (r) =>
  [[r.unit && `U${r.unit}`, r.street_number, r.street_name, r.street_type].filter(Boolean).join(' '), r.suburb, [r.state, r.postcode].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');

// Mirror the driver-portal derivations (keep in sync with DriverLogPage) so the preview
// shows exactly what the driver sees.
const holdPhrase = (r) => {
  const t = (r.article_types || []).join(', ').toLowerCase();
  if (/parcel/.test(t)) return 'hold parcels for this address';
  return 'hold mail for this address';
};
const msFlags = (notes) => {
  const n = (notes || '').toLowerCase();
  return { doNotScan: /do not scan|don't scan/.test(n), doNotEndorse: /endorse|hand ?written/.test(n) };
};

// A faithful preview of the driver's portal card, rendered in the engine's own theme
// (light in light mode) so it matches what the admin is looking at.
function DriverPreview({ row }) {
  const flags = msFlags(row.handling_notes);
  const types = (row.article_types || []).join(', ');
  const RED = '#dc2626';
  const chip = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 600, color: RED };
  const photoBox = { width: 64, height: 64, borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--hp-dim)' };
  const yn = (label, on) => (
    <div style={{ flex: 1, textAlign: 'center', padding: 12, borderRadius: 8, fontWeight: 700, fontSize: 14,
      border: `1px solid ${on ? RED : 'var(--hp-border)'}`, background: on ? RED : 'transparent', color: on ? '#fff' : 'var(--hp-text)' }}>{label}</div>
  );
  return (
    <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 14, padding: 16, maxWidth: 330 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontWeight: 800, color: RED, fontSize: 15 }}>
        <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} /> {row.request_type === 'monitor' ? 'Mail monitor' : 'Mail stopper'} — {holdPhrase(row)}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 10, color: 'var(--hp-text)' }}>{oneLineAddr(row) || '—'}</div>
      {row.addressee && <div style={{ fontSize: 14, marginTop: 4, color: 'var(--hp-text)' }}><span style={{ color: 'var(--hp-dim)' }}>Addressed to:</span> <strong>{row.addressee}</strong></div>}
      {types && <div style={{ fontSize: 12.5, color: 'var(--hp-dim)', marginTop: 4 }}>Watching for: {types}</div>}
      {(flags.doNotScan || flags.doNotEndorse) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {flags.doNotScan && <span style={chip}><ScanLine size={15} /> Do NOT scan the parcel</span>}
          {flags.doNotEndorse && <span style={chip}><Ban size={15} /> Do NOT write on the parcel</span>}
        </div>
      )}
      <div style={{ fontSize: 15, fontWeight: 700, margin: '18px 0 10px', color: 'var(--hp-text)' }}>Did you receive any parcels for this address today?</div>
      <div style={{ display: 'flex', gap: 10 }}>{yn('Yes', true)}{yn('No', false)}</div>
      <div style={{ marginTop: 14, padding: 12, background: 'rgba(220,38,38,0.06)', borderRadius: 8, fontSize: 13, lineHeight: 1.6, color: 'var(--hp-text)' }}>
        Hold the parcel. Do <strong>not</strong> scan it and do <strong>not</strong> write on it. Photograph the parcel and its barcode — add more than one photo if they don't fit in one shot.
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <div style={photoBox}><Camera size={20} /></div>
          <div style={photoBox}><Camera size={20} /></div>
        </div>
        <div style={{ marginTop: 8, height: 40, border: '1px dashed var(--hp-border)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontWeight: 600, color: 'var(--hp-text)', fontSize: 13.5 }}>
          <UploadCloud size={16} /> Add another photo
        </div>
        <div style={{ marginTop: 8, height: 36, border: '1px solid var(--hp-border)', borderRadius: 8, display: 'flex', alignItems: 'center', padding: '0 12px', color: 'var(--hp-dim)', fontSize: 12.5 }}>Barcode number(s) — optional</div>
      </div>
    </div>
  );
}

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%' };
const th = { padding: '9px 10px', textAlign: 'left', fontSize: '.68rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' };
const td = { padding: '10px', borderTop: '1px solid var(--hp-border)', fontSize: '.86rem', verticalAlign: 'middle' };

const STATUS_COLORS = { active: '#16a34a', expired: 'var(--hp-dim)', cancelled: '#b91c1c', completed: '#2563eb' };
// A monitor goes back to AP with its data sheet; offer that from the last few days on.
const returnDue = (r) => r.request_type === 'monitor' && r.status === 'active' && daysLeft(r.to_date) != null && daysLeft(r.to_date) <= 2;
const Badge = ({ text, color }) => (
  <span style={{ fontSize: '.68rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.05em', color, border: `1px solid ${color}`, borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{text}</span>
);

// Days remaining until the ceasing date (negative = past).
const daysLeft = (to) => (to ? Math.ceil((new Date(to + 'T00:00') - new Date(new Date().toDateString())) / 86400000) : null);

const emptyFields = () => ({
  request_type: 'stopper', ap_ref: '', agency: '', article_types: '',
  address: { unit: '', street_number: '', street_name: '', street_type: '', suburb: '', state: 'NSW', postcode: '' },
  addressee: '',
  from_date: '', to_date: '',
  agency_contact: { name: '', email: '', phone: '' }, handling_notes: '',
});

export default function MailStoppersPage() {
  const { addNotification } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  // ?q= deep link from the global search palette pre-fills the search box.
  const [searchParams] = useSearchParams();
  const qParam = searchParams.get('q') || '';
  const [q, setQ] = useState(qParam);
  useEffect(() => { if (qParam) setQ(qParam); }, [qParam]);
  const [drawer, setDrawer] = useState(null); // { mode:'add'|'view', row? }
  const [pageDrag, setPageDrag] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [returning, setReturning] = useState(null); // stopper row being returned to AP

  useEffect(() => { api('driversList').then((r) => { if (r && r.success) setDrivers(r.drivers || []); }).catch(() => {}); }, []);

  // Drop a PDF/image anywhere on the page → open the add drawer, pre-loaded with it.
  const onPageDrop = (e) => {
    e.preventDefault();
    setPageDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    if (!isAllowedFile(f)) { addNotification?.('Please drop a PDF or image', 'error'); return; }
    setDrawer({ mode: 'add', fields: emptyFields(), file: f });
  };

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api('listStoppers', { status: statusFilter });
    if (res.success) setRows(res.stoppers || []);
    else addNotification?.(res.error || 'Failed to load', 'error');
    setLoading(false);
  }, [statusFilter, addNotification]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => `${r.ap_ref} ${r.agency} ${oneLineAddr(r)} ${r.driver_login || ''}`.toLowerCase().includes(needle));
  }, [rows, q]);

  return (
    <div
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setPageDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setPageDrag(false); }}
      onDrop={onPageDrop}
      style={{ position: 'relative', minHeight: '60vh' }}
    >
      {pageDrag && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ border: '2px dashed var(--hp-gold)', borderRadius: 12, padding: '40px 60px', textAlign: 'center', color: '#fff', background: 'var(--hp-surface)' }}>
            <UploadCloud size={40} style={{ color: 'var(--hp-gold)' }} />
            <div style={{ marginTop: 10, fontWeight: 700 }}>Drop the AP request PDF to start a new stopper</div>
            <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>It'll be read and the form pre-filled for you to check</div>
          </div>
        </div>
      )}
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><ShieldAlert size={22} style={{ color: 'var(--hp-gold)' }} /> Mail Stoppers</h2>
          <p>{filtered.length} of {rows.length} shown · Customs / ABF / Police mail interception requests
            {rows.filter((r) => r.receivedToday).length > 0 && <span style={{ color: '#ef4444', fontWeight: 700 }}> · {rows.filter((r) => r.receivedToday).length} parcel{rows.filter((r) => r.receivedToday).length > 1 ? 's' : ''} reported received today</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
          <button className="btn btn-primary" onClick={() => setDrawer({ mode: 'add', fields: emptyFields() })}><Plus size={14} /> Add manually</button>
        </div>
      </div>

      <div className="page-body">
        <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 240px' }}>
            <label style={labelStyle}>Search ref / agency / address</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
              <input className="input" style={{ ...inputStyle, paddingLeft: 30 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Kent St, 3192156, Border Force…" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select className="input" style={{ ...inputStyle, width: 140 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 26, color: 'var(--hp-dim)' }}>
              {rows.length === 0 ? <>No mail stoppers. They arrive by email automatically, or click <strong>Add manually</strong>.</> : 'None match your search.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                <thead>
                  <tr>
                    <th style={th}>Window</th>
                    <th style={th}>Ref</th>
                    <th style={th}>Type</th>
                    <th style={th}>Address</th>
                    <th style={th}>Driver</th>
                    <th style={th}>Status</th>
                    <th style={{ ...th, textAlign: 'right' }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const dl = daysLeft(r.to_date);
                    const expiringSoon = r.status === 'active' && dl != null && dl >= 0 && dl <= 3;
                    return (
                      <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setDrawer({ mode: 'view', id: r.id })}>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Calendar size={12} style={{ color: 'var(--hp-dim)' }} />
                            <span>{fmtDate(r.from_date)} – {fmtDate(r.to_date)}</span>
                          </div>
                          {expiringSoon && <span style={{ fontSize: '.7rem', color: '#d97706' }}>{dl === 0 ? 'ends today' : `${dl}d left`}</span>}
                        </td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{r.ap_ref || '—'}</td>
                        <td style={td}>{r.request_type === 'monitor' ? 'Monitor' : 'Stopper'}</td>
                        <td style={{ ...td, fontWeight: 600 }}>
                          {oneLineAddr(r) || '—'}
                          {r.attachment_path && <FileText size={12} style={{ marginLeft: 6, color: 'var(--hp-dim)' }} title="Has PDF" />}
                          {r.receivedToday && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8, background: '#ef4444', color: '#fff', fontSize: '.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', borderRadius: 5, padding: '2px 6px', verticalAlign: 'middle' }}>
                              <AlertTriangle size={11} /> Parcel received
                            </span>
                          )}
                          {r.addressee && <div style={{ fontSize: '.76rem', fontWeight: 400, color: 'var(--hp-dim)', marginTop: 2 }}>{r.addressee}</div>}
                        </td>
                        <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: '.8rem' }}>
                          {r.match_status === 'matched'
                            ? <span>
                                {r.driver_login}
                                {r.status === 'active' && (
                                  r.answeredToday
                                    ? <span style={{ marginLeft: 6, color: '#16a34a', fontSize: '.68rem' }} title="Driver answered the Onboard check today">✓ checked in</span>
                                    : <span style={{ marginLeft: 6, color: 'var(--hp-dim)', fontSize: '.68rem' }} title="Driver hasn't answered today's Onboard check yet">· awaiting check-in</span>
                                )}
                              </span>
                            : <span style={{ color: '#d97706', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <AlertTriangle size={12} />{r.match_status === 'suggested' ? `${suggestion(r).suggested_driver}?` : r.match_status === 'ambiguous' ? 'pick' : 'assign'}
                              </span>}
                        </td>
                        <td style={td}><Badge text={r.status} color={STATUS_COLORS[r.status] || 'var(--hp-dim)'} /></td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {returnDue(r) && (
                            <button className="btn btn-primary btn-sm" style={{ marginRight: 8 }} title="Fill AP's Mail Monitor Data Sheet and email it back"
                              onClick={(e) => { e.stopPropagation(); setReturning(r); }}>
                              <Send size={13} /> {daysLeft(r.to_date) < 0 ? 'Ended — send sheet' : 'Send sheet'}
                            </button>
                          )}
                          <ExternalLink size={14} style={{ color: 'var(--hp-dim)' }} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {drawer && (
        <StopperDrawer
          drawer={drawer}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); load(); }}
          notify={addNotification}
          drivers={drivers}
          onReturn={(r) => setReturning(r)}
        />
      )}

      {returning && (
        <MailMonitorReturn
          stopper={returning}
          notify={addNotification}
          onClose={() => setReturning(null)}
          onSent={() => { setReturning(null); setDrawer(null); load(); }}
        />
      )}
    </div>
  );
}

function StopperDrawer({ drawer, onClose, onSaved, notify, drivers = [], onReturn }) {
  const [busy, setBusy] = useState(false);
  const [row, setRow] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [checks, setChecks] = useState([]);
  const [edit, setEdit] = useState(drawer.mode === 'add');
  const [fields, setFields] = useState(drawer.fields || emptyFields());
  const [file, setFile] = useState(null);   // { base64, name, contentType }
  const [reading, setReading] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const [assignSel, setAssignSel] = useState('');
  const [reassign, setReassign] = useState(false);
  const [matching, setMatching] = useState(false);
  const [teamLeader, setTeamLeader] = useState(null); // { area, defaultLogin, override, resolvedLogin }
  const [leaders, setLeaders] = useState([]);          // team-leader logins for the dropdown

  // Read a dropped/chosen PDF or image: keep it for upload on save, and ask the server
  // to extract fields from it to pre-fill the form (only filling blanks so typed values win).
  const processFile = useCallback(async (f) => {
    if (!f) return;
    if (!isAllowedFile(f)) { notify?.('Please choose a PDF or image', 'error'); return; }
    if (f.size > MAX_MB * 1024 * 1024) { notify?.(`File too large (max ${MAX_MB} MB)`, 'error'); return; }
    setReading(true);
    try {
      const { base64, contentType, name } = await fileToBase64(f);
      setFile({ base64, contentType, name });
      const res = await api('extractFile', { fileBase64: base64, contentType });
      if (res.success && res.fields) {
        const x = res.fields;
        const keep = (cur, val) => (cur && String(cur).trim() ? cur : (val || ''));
        setFields((cur) => ({
          request_type: x.request_type || cur.request_type,
          ap_ref: keep(cur.ap_ref, x.ap_ref),
          agency: keep(cur.agency, x.agency),
          article_types: cur.article_types && cur.article_types.trim() ? cur.article_types : (x.article_types || []).join(', '),
          address: {
            unit: keep(cur.address.unit, x.address?.unit),
            street_number: keep(cur.address.street_number, x.address?.street_number),
            street_name: keep(cur.address.street_name, x.address?.street_name),
            street_type: keep(cur.address.street_type, x.address?.street_type),
            suburb: keep(cur.address.suburb, x.address?.suburb),
            state: keep(cur.address.state, x.address?.state) || 'NSW',
            postcode: keep(cur.address.postcode, x.address?.postcode),
          },
          addressee: keep(cur.addressee, x.addressee),
          from_date: keep(cur.from_date, x.from_date),
          to_date: keep(cur.to_date, x.to_date),
          agency_contact: {
            name: keep(cur.agency_contact.name, x.agency_contact?.name),
            email: keep(cur.agency_contact.email, x.agency_contact?.email),
            phone: keep(cur.agency_contact.phone, x.agency_contact?.phone),
          },
          handling_notes: keep(cur.handling_notes, x.handling_notes),
        }));
        notify?.(res.meta?.usedClaude ? 'Read the file and pre-filled the form — please check it' : 'File attached', 'success');
      } else {
        notify?.(res.error || 'Could not read the file, but it will still be attached', 'error');
      }
    } catch (e) {
      notify?.('Failed to read the file', 'error');
    } finally {
      setReading(false);
    }
  }, [notify]);

  // If the drawer was opened by dropping a file on the page, process it now.
  useEffect(() => { if (drawer.file) processFile(drawer.file); }, [drawer.file, processFile]);

  useEffect(() => {
    if (drawer.mode !== 'view') return;
    (async () => {
      const res = await api('getStopper', { id: drawer.id });
      if (res.success) {
        setRow(res.stopper);
        setAttachmentUrl(res.attachmentUrl || '');
        setChecks(res.checks || []);
        setTeamLeader(res.teamLeader || null);
        setLeaders(res.leaders || []);
        setAssignSel(res.stopper.driver_login || suggestion(res.stopper).suggested_driver || '');
        const s = res.stopper;
        setFields({
          request_type: s.request_type, ap_ref: s.ap_ref, agency: s.agency,
          article_types: (s.article_types || []).join(', '),
          address: { unit: s.unit, street_number: s.street_number, street_name: s.street_name, street_type: s.street_type, suburb: s.suburb, state: s.state, postcode: s.postcode },
          addressee: s.addressee || '',
          from_date: s.from_date || '', to_date: s.to_date || '',
          agency_contact: s.agency_contact || { name: '', email: '', phone: '' },
          handling_notes: s.handling_notes || '',
        });
        // Auto-find the driver the moment the drawer opens (for rows that predate
        // matching, or arrived without a suggestion) — so you just click Confirm.
        if (s.match_status !== 'matched' && !suggestion(s).suggested_driver) {
          setMatching(true);
          const rm = await api('rematch', { id: drawer.id });
          setMatching(false);
          if (rm && rm.success) {
            setRow((prev) => prev && ({ ...prev, match_status: rm.match_status, canon_street: rm.canon_street,
              extracted: { ...(prev.extracted || {}), matchSuggestion: { suggested_driver: rm.suggested_driver, candidates: rm.match_candidates, via: rm.via } } }));
            setAssignSel((prev) => prev || rm.suggested_driver || '');
          }
        }
      } else notify?.(res.error || 'Not found', 'error');
    })();
  }, [drawer, notify]);

  const setAddr = (k, v) => setFields((f) => ({ ...f, address: { ...f.address, [k]: v } }));
  const setContact = (k, v) => setFields((f) => ({ ...f, agency_contact: { ...f.agency_contact, [k]: v } }));

  const save = async () => {
    setBusy(true);
    const fileParams = file ? { fileBase64: file.base64, filename: file.name, contentType: file.contentType } : {};
    const res = drawer.mode === 'add'
      ? await api('addManualStopper', { fields, ...fileParams })
      : await api('updateStopper', { id: drawer.id, fields, ...fileParams });
    setBusy(false);
    if (res.success) { notify?.(drawer.mode === 'add' ? 'Mail stopper added' : 'Saved', 'success'); onSaved(); }
    else notify?.(res.error || 'Save failed', 'error');
  };

  const confirmDriver = async (loginArg) => {
    const login = ((typeof loginArg === 'string' ? loginArg : assignSel) || '').trim().toUpperCase();
    if (!login) { notify?.('Pick or type a driver login', 'error'); return; }
    setBusy(true);
    const res = await api('assignDriver', { id: drawer.id, driver_login: login });
    setBusy(false);
    if (res.success) { notify?.(`Assigned to ${login}`, 'success'); onSaved(); }
    else notify?.(res.error || 'Failed', 'error');
  };
  // Change (or clear) the covering team leader for this stopper, then refresh in place.
  const changeLeader = async (val) => {
    setBusy(true);
    const res = await api('assignLeader', { id: drawer.id, team_leader: val, driver_login: row?.driver_login || '' });
    if (res.success) {
      const g = await api('getStopper', { id: drawer.id });
      if (g.success) { setRow(g.stopper); setTeamLeader(g.teamLeader || null); }
      notify?.('Team leader updated', 'success');
    } else notify?.(res.error || 'Failed', 'error');
    setBusy(false);
  };

  const doRematch = async () => {
    setBusy(true);
    const res = await api('rematch', { id: drawer.id });
    setBusy(false);
    if (res.success) { notify?.('Re-checked the route match', 'success'); onSaved(); }
    else notify?.(res.error || 'Failed', 'error');
  };

  const setStatus = async (status) => {
    setBusy(true);
    const res = await api('updateStopper', { id: drawer.id, status });
    setBusy(false);
    if (res.success) { notify?.(`Marked ${status}`, 'success'); onSaved(); }
    else notify?.(res.error || 'Failed', 'error');
  };

  const del = async () => {
    if (!window.confirm('Delete this mail stopper permanently?')) return;
    setBusy(true);
    const res = await api('deleteStopper', { id: drawer.id });
    setBusy(false);
    if (res.success) { notify?.('Deleted', 'success'); onSaved(); }
    else notify?.(res.error || 'Delete failed', 'error');
  };

  const dl = row ? daysLeft(row.to_date) : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div className="card" style={{ width: 'min(560px, 100%)', height: '100%', overflowY: 'auto', borderRadius: 0, padding: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <ShieldAlert size={18} style={{ color: 'var(--hp-gold)' }} />
            {drawer.mode === 'add' ? 'Add mail stopper' : `Ref ${row?.ap_ref || '—'}`}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {drawer.mode === 'view' && !row ? (
          <div style={{ color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
        ) : (
          <>
            {drawer.mode === 'view' && !edit && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
                {row.request_type === 'monitor' && (() => {
                  const sends = (row.extracted && row.extracted.monitorReturn && row.extracted.monitorReturn.sends) || [];
                  const last = sends[sends.length - 1];
                  return (
                    <div style={{ border: `1px solid ${returnDue(row) ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 8, padding: 12 }}>
                      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><Send size={12} /> Return to AP</div>
                      <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 10, lineHeight: 1.5 }}>
                        {last
                          ? <>Sheet sent {new Date(last.at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })} to {last.to.join(', ')}{last.cc && last.cc.length ? `, cc ${last.cc.join(', ')}` : ''} · {last.lines} lines{last.photos ? `, ${last.photos} photo${last.photos > 1 ? 's' : ''}` : ''}.</>
                          : <>At the end of the monitor, AP's Mail Monitor Data Sheet goes back filled in — one line per day — with a photo of anything held.</>}
                      </div>
                      <button className={`btn ${returnDue(row) ? 'btn-primary' : 'btn-secondary'}`} style={{ width: '100%', justifyContent: 'center' }} onClick={() => onReturn && onReturn(row)}>
                        <Send size={14} /> {row.status === 'active' ? 'Stop monitor & send sheet to AP…' : last ? 'Send the sheet again…' : 'Send sheet to AP…'}
                      </button>
                    </div>
                  );
                })()}
                {row.parse_error && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: 'rgba(217,119,6,.1)', border: '1px solid #d97706', borderRadius: 6, padding: '8px 10px', fontSize: '.82rem' }}>
                    <AlertTriangle size={15} style={{ color: '#d97706', flexShrink: 0, marginTop: 1 }} />
                    <span>Auto-read left gaps: {row.parse_error}. Check the fields and <strong>Edit</strong> to correct.</span>
                  </div>
                )}
                <div style={{ border: `1px solid ${row.match_status === 'matched' ? 'var(--hp-border)' : '#d97706'}`, borderRadius: 8, padding: 12 }}>
                  <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><User size={12} /> Driver</div>
                  {row.match_status === 'matched' && !reassign ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.95rem' }}>
                      <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{row.driver_login}</span>
                      <span style={{ fontSize: '.72rem', color: '#16a34a' }}>confirmed</span>
                      <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setReassign(true)}>Change</button>
                    </div>
                  ) : matching ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.9rem', color: 'var(--hp-dim)' }}>
                      <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Finding the driver whose route covers this address…
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                        Searched routes for <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-text)' }}>{[row.street_number, row.street_name, row.street_type].filter(Boolean).join(' ') || '(no street)'}</span>
                      </div>
                      {suggestion(row).suggested_driver ? (
                        <>
                          <div style={{ fontSize: '.82rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                            {row.match_status === 'ambiguous' ? 'Shared street — best match by house number' : 'Matched from the driver routes'}
                            {suggestion(row).via && <> · covers <span style={{ fontFamily: 'var(--font-mono)' }}>{suggestion(row).via}</span></>}
                          </div>
                          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={busy}
                            onClick={() => confirmDriver(suggestion(row).suggested_driver)}>
                            <Check size={15} /> Confirm {suggestion(row).suggested_driver}
                          </button>
                        </>
                      ) : (
                        <div style={{ fontSize: '.82rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                          No route covers this address. Pick a driver below, or <button className="btn btn-ghost btn-sm" onClick={doRematch}>re-check</button>.
                        </div>
                      )}

                      {(suggestion(row).candidates || []).filter((c) => c !== suggestion(row).suggested_driver).length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 6 }}>Another driver on this street?</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {suggestion(row).candidates.filter((c) => c !== suggestion(row).suggested_driver).map((c) => (
                              <button key={c} onClick={() => confirmDriver(c)} disabled={busy}
                                style={{ fontSize: '.78rem', padding: '5px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-text)', fontFamily: 'var(--font-mono)' }}>
                                {c}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <select className="input" style={inputStyle} value={assignSel} onChange={(e) => setAssignSel(e.target.value)}>
                          <option value="">Choose another driver…</option>
                          {drivers.map((d) => <option key={d.login} value={d.login}>{d.name ? `${d.login} · ${d.name}` : d.login}</option>)}
                        </select>
                        <button className="btn btn-secondary" disabled={busy} onClick={() => confirmDriver()}>Assign</button>
                        {reassign && <button className="btn btn-ghost" onClick={() => setReassign(false)}>Cancel</button>}
                      </div>
                    </>
                  )}
                </div>
                {row.driver_login && teamLeader && (() => {
                  const nameFor = (login) => { const d = drivers.find((x) => x.login === login); return d && d.name ? `${login} · ${d.name}` : login; };
                  const areaLabel = { DARL: 'Darlinghurst', BWAY: 'Broadway', CITY: 'City' }[teamLeader.area] || teamLeader.area || '';
                  const sel = teamLeader.override || ''; // '' default · 'NONE' · a login
                  return (
                    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12 }}>
                      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><Users size={12} /> Also visible to team leader</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                        If {row.driver_login} is away, the team leader can hold the parcel and record it.
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: '.9rem' }}>
                        {teamLeader.resolvedLogin ? (
                          <>
                            <span style={{ fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{nameFor(teamLeader.resolvedLogin)}</span>
                            <span style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>
                              {teamLeader.override ? 'custom' : `default${areaLabel ? ` · ${areaLabel}` : ''}`}
                            </span>
                          </>
                        ) : <span style={{ color: 'var(--hp-dim)' }}>No team leader — not shown to a leader</span>}
                      </div>
                      <select className="input" style={inputStyle} value={sel} disabled={busy} onChange={(e) => changeLeader(e.target.value)}>
                        <option value="">Default{teamLeader.defaultLogin ? ` — ${nameFor(teamLeader.defaultLogin)}` : ' — no leader for this area'}</option>
                        {leaders.length > 0 && (
                          <optgroup label="Team leaders">
                            {leaders.map((l) => <option key={l} value={l}>{nameFor(l)}</option>)}
                          </optgroup>
                        )}
                        <option value="NONE">None — don't show to a leader</option>
                      </select>
                    </div>
                  );
                })()}
                <div>
                  <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><ShieldAlert size={12} /> What the driver sees in their portal</div>
                  <DriverPreview row={row} />
                </div>
                <Field icon={Calendar} label="Window">{fmtDate(row.from_date)} – {fmtDate(row.to_date)} {dl != null && row.status === 'active' && <span style={{ color: dl <= 3 ? '#d97706' : 'var(--hp-dim)' }}>· {dl < 0 ? 'ended' : dl === 0 ? 'ends today' : `${dl}d left`}</span>}</Field>
                <Field icon={ShieldAlert} label="Type">{row.request_type === 'monitor' ? 'Mail Monitor' : 'Mail Stopper'}</Field>
                <Field icon={MapPin} label="Address">{oneLineAddr(row) || '—'}</Field>
                <Field icon={User} label="Addressed to">{row.addressee || '—'}</Field>
                <Field icon={Building} label="Agency">{row.agency || '—'}</Field>
                <Field icon={FileText} label="Article types">{(row.article_types || []).join(', ') || '—'}</Field>
                <Field icon={User} label="Agency contact">
                  {row.agency_contact?.name || '—'}
                  {row.agency_contact?.email && <><br />{row.agency_contact.email}</>}
                  {row.agency_contact?.phone && <><br />{row.agency_contact.phone}</>}
                </Field>
                {row.handling_notes && <Field icon={AlertTriangle} label="Handling">{row.handling_notes}</Field>}
                <div style={{ display: 'flex', gap: 8, fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>
                  <span>Status: {row.status}</span>·<span>Match: {row.match_status}</span>·<span>Source: {row.source || 'manual'}</span>
                </div>
                {checks.length > 0 && (
                  <div>
                    <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><Camera size={12} /> Driver check-ins</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {checks.map((c) => (
                        <div key={c.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem' }}>
                            <span style={{ fontFamily: 'var(--font-mono)' }}>{c.driver_login}</span>
                            <span style={{ color: 'var(--hp-dim)' }}>{fmtDate(c.check_date)}</span>
                            <span style={{ marginLeft: 'auto', fontWeight: 700, color: c.received ? '#b91c1c' : '#16a34a' }}>
                              {c.received ? 'Parcels received' : 'None received'}
                            </span>
                          </div>
                          {c.barcodes && <div style={{ fontSize: '.8rem', marginTop: 4 }}>Barcodes: <span style={{ fontFamily: 'var(--font-mono)' }}>{c.barcodes}</span></div>}
                          {(c.photos || []).length > 0 && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                              {c.photos.map((p, i) => (
                                <a key={i} href={p} target="_blank" rel="noreferrer">
                                  <img src={p} alt="parcel" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {attachmentUrl && (
                  <a className="btn btn-secondary btn-sm" href={attachmentUrl} target="_blank" rel="noreferrer" style={{ alignSelf: 'flex-start' }}>
                    <FileText size={14} /> View original PDF
                  </a>
                )}
              </div>
            )}

            {(edit || drawer.mode === 'add') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
                <label
                  onDragOver={(e) => { e.preventDefault(); setDropOver(true); }}
                  onDragLeave={() => setDropOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDropOver(false); processFile(e.dataTransfer?.files?.[0]); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    border: `2px dashed ${dropOver ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                    background: dropOver ? 'rgba(212,175,55,.08)' : 'transparent',
                    borderRadius: 8, padding: '14px 16px', transition: 'all .12s',
                  }}
                >
                  <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                    onChange={(e) => { processFile(e.target.files?.[0]); e.target.value = ''; }} />
                  {reading ? <Loader size={18} style={{ color: 'var(--hp-gold)' }} /> : <UploadCloud size={18} style={{ color: 'var(--hp-gold)' }} />}
                  <div style={{ fontSize: '.85rem' }}>
                    {reading ? 'Reading the file…'
                      : file ? <><strong>{file.name}</strong> attached — drop another to replace</>
                      : <>Drag the AP request <strong>PDF</strong> here, or click to choose — it'll pre-fill the form</>}
                  </div>
                  {file && !reading && (
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                      onClick={(e) => { e.preventDefault(); setFile(null); }}><X size={13} /></button>
                  )}
                </label>
                <Row>
                  <Col label="Request type"><select className="input" style={inputStyle} value={fields.request_type} onChange={(e) => setFields({ ...fields, request_type: e.target.value })}><option value="stopper">Mail Stopper</option><option value="monitor">Mail Monitor</option></select></Col>
                  <Col label="AP ref"><input className="input" style={inputStyle} value={fields.ap_ref} onChange={(e) => setFields({ ...fields, ap_ref: e.target.value })} /></Col>
                </Row>
                <Col label="Agency"><input className="input" style={inputStyle} value={fields.agency} onChange={(e) => setFields({ ...fields, agency: e.target.value })} placeholder="Australian Border Force (Federal)" /></Col>
                <Col label="Article types (comma-separated)"><input className="input" style={inputStyle} value={fields.article_types} onChange={(e) => setFields({ ...fields, article_types: e.target.value })} placeholder="International Letters, International Parcels" /></Col>
                <Row>
                  <Col label="Unit" flex="0 0 70px"><input className="input" style={inputStyle} value={fields.address.unit} onChange={(e) => setAddr('unit', e.target.value)} /></Col>
                  <Col label="Street no." flex="0 0 90px"><input className="input" style={inputStyle} value={fields.address.street_number} onChange={(e) => setAddr('street_number', e.target.value)} /></Col>
                  <Col label="Street name"><input className="input" style={inputStyle} value={fields.address.street_name} onChange={(e) => setAddr('street_name', e.target.value)} /></Col>
                  <Col label="Type" flex="0 0 90px"><input className="input" style={inputStyle} value={fields.address.street_type} onChange={(e) => setAddr('street_type', e.target.value)} placeholder="Street" /></Col>
                </Row>
                <Col label="Addressed to (name on the mail)"><input className="input" style={inputStyle} value={fields.addressee} onChange={(e) => setFields({ ...fields, addressee: e.target.value })} placeholder="Person, business or building name" /></Col>
                <Row>
                  <Col label="Suburb"><input className="input" style={inputStyle} value={fields.address.suburb} onChange={(e) => setAddr('suburb', e.target.value)} /></Col>
                  <Col label="State" flex="0 0 80px"><input className="input" style={inputStyle} value={fields.address.state} onChange={(e) => setAddr('state', e.target.value)} /></Col>
                  <Col label="Postcode" flex="0 0 100px"><input className="input" style={inputStyle} value={fields.address.postcode} onChange={(e) => setAddr('postcode', e.target.value)} /></Col>
                </Row>
                <Row>
                  <Col label="From date"><input type="date" className="input" style={inputStyle} value={fields.from_date} onChange={(e) => setFields({ ...fields, from_date: e.target.value })} /></Col>
                  <Col label="To date"><input type="date" className="input" style={inputStyle} value={fields.to_date} onChange={(e) => setFields({ ...fields, to_date: e.target.value })} /></Col>
                </Row>
                <Row>
                  <Col label="Contact name"><input className="input" style={inputStyle} value={fields.agency_contact.name} onChange={(e) => setContact('name', e.target.value)} /></Col>
                  <Col label="Contact email"><input className="input" style={inputStyle} value={fields.agency_contact.email} onChange={(e) => setContact('email', e.target.value)} /></Col>
                </Row>
                <Col label="Contact phone"><input className="input" style={inputStyle} value={fields.agency_contact.phone} onChange={(e) => setContact('phone', e.target.value)} /></Col>
                <Col label="Handling notes"><textarea className="input" style={{ ...inputStyle, minHeight: 60 }} value={fields.handling_notes} onChange={(e) => setFields({ ...fields, handling_notes: e.target.value })} /></Col>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(edit || drawer.mode === 'add') ? (
                <>
                  <button className="btn btn-primary" disabled={busy} onClick={save}><Save size={14} /> {drawer.mode === 'add' ? 'Add stopper' : 'Save changes'}</button>
                  {drawer.mode === 'view' && <button className="btn btn-ghost" disabled={busy} onClick={() => setEdit(false)}>Cancel</button>}
                </>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={() => setEdit(true)}>Edit</button>
                  {row.status === 'active' && <button className="btn btn-ghost" disabled={busy} onClick={() => setStatus('cancelled')}><Ban size={14} /> Cancel stopper</button>}
                  {row.status !== 'active' && <button className="btn btn-ghost" disabled={busy} onClick={() => setStatus('active')}>Re-activate</button>}
                  <button className="btn btn-ghost" disabled={busy} onClick={del} style={{ color: '#b91c1c', marginLeft: 'auto' }}><Trash2 size={14} /> Delete</button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 6 }}><Icon size={12} /> {label}</div>
    <div style={{ fontSize: '.9rem' }}>{children}</div>
  </div>
);
const Row = ({ children }) => <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{children}</div>;
const Col = ({ label, children, flex = '1 1 120px' }) => (
  <div style={{ flex }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);
